import React, { Component } from "react";
import PropTypes from "prop-types";
import { Button, Icon } from "semantic-ui-react";
import { SearchBar } from "react-searchkit";
import { SearchHistoryModal } from "@modules/SearchControls/SearchHistory";
import { authenticationService } from "@authentication/services/AuthenticationService";

// Constants
const DEFAULT_SEARCH_HISTORY_PARAMS = {
  days: 30,
  search_term: "",
  sort_by: "date",
  sort_order: "desc",
  page: 1,
  per_page: 10,
  success_only: false,
};

const DEFAULT_SEARCH_STATS_PARAMS = {
  days: 30,
  include_popular: true,
  include_recent: true,
  popular_limit: 10,
  recent_limit: 20,
};

const REQUEST_TIMEOUT = 10000;

export class ReactSearchKitEnhancedSearchBar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showSearchHistory: false,
      searchHistory: [],
      searchStats: null,
      isLoadingHistory: false,
      isLoadingStats: false,
      error: null,
    };
    this.cancelTokenSource = null;
  }

  componentDidMount() {
    if (this.props.searchHistoryEnabled && this.props.searchHistoryApi) {
      // Only load data if user is authenticated
      if (this.isUserAuthenticated()) {
        this.loadInitialData();
      }
    }
  }

  componentWillUnmount() {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel("Component unmounting");
    }
  }

  isUserAuthenticated = () => {
    return authenticationService.isAuthenticated();
  };

  loadInitialData = async () => {
    if (!this.isUserAuthenticated()) {
      return;
    }

    try {
      await Promise.all([this.loadSearchHistory(), this.loadSearchStats()]);
    } catch (error) {
      // Error handled silently - 401 errors are expected when not authenticated
    }
  };

  createTimeoutPromise = (timeout = REQUEST_TIMEOUT) => {
    return new Promise((_, reject) =>
      setTimeout(
        () =>
          reject(new Error(`Request timeout after ${timeout / 1000} seconds`)),
        timeout
      )
    );
  };

  loadSearchHistory = async (params = {}) => {
    if (!this.props.searchHistoryApi?.list) return;

    if (!this.isUserAuthenticated()) {
      this.setState({
        searchHistory: [],
        isLoadingHistory: false,
        error: null,
      });
      return;
    }

    this.setState({ isLoadingHistory: true, error: null });

    try {
      const requestParams = { ...DEFAULT_SEARCH_HISTORY_PARAMS, ...params };

      const response = await Promise.race([
        this.props.searchHistoryApi.list(requestParams),
        this.createTimeoutPromise(),
      ]);

      const items = response.data?.hits || [];

      this.setState({
        searchHistory: items,
        isLoadingHistory: false,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        this.setState({
          searchHistory: [],
          isLoadingHistory: false,
          error: null,
        });
      } else {
        this.setState({
          searchHistory: [],
          isLoadingHistory: false,
          error: error.message || "Failed to load search history",
        });
      }
    }
  };

  loadSearchStats = async (params = {}) => {
    if (!this.props.searchHistoryApi?.stats) return;

    if (!this.isUserAuthenticated()) {
      this.setState({
        searchStats: null,
        isLoadingStats: false,
      });
      return;
    }

    this.setState({ isLoadingStats: true });

    try {
      const requestParams = { ...DEFAULT_SEARCH_STATS_PARAMS, ...params };
      const response = await this.props.searchHistoryApi.stats(requestParams);

      this.setState({
        searchStats: response.data,
        isLoadingStats: false,
      });
    } catch (error) {
      this.setState({
        searchStats: null,
        isLoadingStats: false,
      });
    }
  };

  refreshData = () => {
    if (this.isUserAuthenticated()) {
      this.loadSearchHistory();
      this.loadSearchStats();
    }
  };

  toggleSearchHistory = () => {
    if (!this.isUserAuthenticated()) {
      return;
    }

    const newShowState = !this.state.showSearchHistory;
    this.setState({ showSearchHistory: newShowState });

    if (newShowState) {
      this.refreshData();
    }
  };

  handleRepeatSearch = (query) => {
    this.setState({ showSearchHistory: false });

    if (this.props.onRepeatSearch) {
      this.props.onRepeatSearch(query);
    }
  };

  handleDeleteSearchEntry = async (entryId) => {
    if (!this.props.searchHistoryApi?.delete || !this.isUserAuthenticated())
      return;

    try {
      await this.props.searchHistoryApi.delete(entryId);
      await this.refreshData();
    } catch (error) {
      // Error handled silently
    }
  };

  handleClearSearchHistory = async () => {
    if (
      !this.props.searchHistoryApi?.clearHistory ||
      !this.isUserAuthenticated()
    )
      return;

    try {
      await this.props.searchHistoryApi.clearHistory();
      await this.refreshData();
    } catch (error) {
      // Error handled silently
    }
  };

  handleSearchInHistory = async (searchTerm, params = {}) => {
    if (
      !this.props.searchHistoryApi?.searchInHistory ||
      !this.isUserAuthenticated()
    ) {
      return [];
    }

    try {
      const response = await this.props.searchHistoryApi.searchInHistory(
        searchTerm,
        params
      );
      return response.data || [];
    } catch (error) {
      return [];
    }
  };

  render() {
    const {
      onRepeatSearch,
      onSearchSubmit,
      searchHistoryEnabled = false,
      searchHistoryApi,
      ...searchBarProps
    } = this.props;

    const {
      showSearchHistory,
      searchHistory,
      searchStats,
      isLoadingHistory,
      isLoadingStats,
      error,
    } = this.state;

    const isAuthenticated = this.isUserAuthenticated();
    const showHistoryButton = searchHistoryEnabled && isAuthenticated;

    return (
      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", alignItems: "stretch", gap: "0.5em" }}>
          <div style={{ flex: 1 }}>
            {/* FIX: Remove ref entirely since SearchBar doesn't support refs */}
            <SearchBar {...searchBarProps} />
          </div>
          {showHistoryButton && (
            <Button
              icon
              onClick={this.toggleSearchHistory}
              title="View Search History"
              size="big"
              style={{
                padding: "0.78em 0.78em",
                borderRadius: "0.28571429rem",
              }}
            >
              <Icon name="history" />
            </Button>
          )}
        </div>

        {showHistoryButton && (
          <SearchHistoryModal
            open={showSearchHistory}
            onClose={() => this.setState({ showSearchHistory: false })}
            onSearchExecute={this.handleRepeatSearch}
            onDeleteEntry={this.handleDeleteSearchEntry}
            onClearHistory={this.handleClearSearchHistory}
            onSearchInHistory={this.handleSearchInHistory}
            onRefresh={this.refreshData}
            searchHistory={searchHistory}
            searchStats={searchStats}
            isLoadingHistory={isLoadingHistory}
            isLoadingStats={isLoadingStats}
            error={error}
          />
        )}
      </div>
    );
  }
}

ReactSearchKitEnhancedSearchBar.propTypes = {
  placeholder: PropTypes.string,
  onRepeatSearch: PropTypes.func,
  onSearchSubmit: PropTypes.func,
  searchHistoryEnabled: PropTypes.bool,
  searchHistoryApi: PropTypes.object,
};

ReactSearchKitEnhancedSearchBar.defaultProps = {
  onRepeatSearch: null,
  onSearchSubmit: null,
  searchHistoryEnabled: false,
  searchHistoryApi: null,
};
